import * as z from 'zod';

// A merchant staff member (сотрудник) on the "Сотрудники" screen.
//
// This is the slice's VIEW MODEL, not the wire shape — `@noor/eats-api`'s
// `employeeSchema` owns that, and `lib/map-user.ts` maps between them. The
// fields here are exactly what the roster screens render, which is a strict
// subset of what the Figma shows: the designs draw an EMAIL row on the card
// and an email field in the form, and `v1Employee` has no email at all, so it
// is absent here rather than modelled and left permanently blank.

/**
 * The staff roles, in the backend's own vocabulary (auth-service names them
 * `merchant-admin | manager | cashier`). Kept verbatim rather than translated
 * into a local vocabulary: the create payload sends this string straight
 * through, so a second set of names would only be a mapping to get wrong.
 *
 * `unknown` is the local addition — `v1Employee.role` is an open string on the
 * wire, so a role the panel does not know about renders as "—" instead of
 * failing the roster parse.
 */
export const userRoleSchema = z.enum(['merchant-admin', 'manager', 'cashier', 'unknown']);
export type UserRole = z.infer<typeof userRoleSchema>;

/**
 * The role tabs across the top of the desktop table.
 *
 * One tab per assignable role plus `admins` for `merchant-admin`. The previous
 * model had an `operators`/`managers` split that no backend role corresponded
 * to; these map 1:1 onto what the API can actually return.
 *
 * `all` is the exception: it is a VIEW, not a role, so it has no `ROLE_TAB`
 * entry and no user is ever mapped to it — `filterUsers` special-cases it
 * instead. It is the default, since the roster is small and "who works here"
 * is the question the screen opens on; landing on «Админы» hid every manager
 * and cashier behind a tab the merchant had to know to click.
 */
export const userTabSchema = z.enum(['all', 'admins', 'managers', 'cashiers']);
export type UserTab = z.infer<typeof userTabSchema>;

export const userSchema = z.object({
  /** `employee_id` — a UUID string; the only id the write endpoints accept. */
  id: z.string(),
  /** `first_name` + `last_name`, joined for display. May be empty — the attach
   *  endpoint allows a nameless identity, filled in later via a rename. */
  fullName: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: userRoleSchema,
  /** E.164 from the wire (`+998901234567`); formatted at the view. */
  phone: z.string(),
  /** Lifecycle string from the wire, rendered only as a muted caption. */
  status: z.string(),
});
export type User = z.infer<typeof userSchema>;

export const usersSchema = z.object({
  items: z.array(userSchema),
});
export type Users = z.infer<typeof usersSchema>;
